import type { Comment, Person } from 'schema-dts';

import type { Message, User } from '../../types/api';
import { stripHtml } from '../../utils/dom';
import { absoluteUrl, profileUrl } from '../../utils/url';
import { mkMessageAnchor } from '../ThreadMessages/messageAnchor';

// `absoluteUrl` vit désormais dans `utils/url` (source unique, B6_12) — ré-exporté ici pour la
// compatibilité des imports historiques (B8_01/C13, tests).
export { absoluteUrl };

// Convergence du jalon 1 (B1_01 × B1_02) : le helper local `messageAnchorId` a été remplacé par
// `mkMessageAnchor`, seul point d'émission de la convention `message_<id>` (contrat C1) — le module
// qui l'héberge sait AUSSI relire l'ancre côté lecteur, ce qui en fait le bon foyer. On évite ainsi
// la classe « deux points d'émission qui dérivent » que `utils/url` combat (elle a déjà mordu deux
// fois). ⚠ Convention concurrente `msg_<id>` (`mkMessageDOMId`, thread-service) NON retenue par C1.

/**
 * Transforme la liste de réponses d'un thread en `Comment[]` schema.org, pour le `comment[]` du
 * `DiscussionForumPosting` (forum). Extrait de `ThreadMessagesLd` (B8_01/C13) pour être réutilisable
 * hors composant (appelé côté `TopicLd` en SSR) : fonction pure, `fullBasepathUrl` passé en paramètre.
 */
export function messagesToComments(
  messages: Message[],
  { fullBasepathUrl, topicSlug }: { fullBasepathUrl: string; topicSlug: string },
): Comment[] {
  const topicUrl = absoluteUrl(fullBasepathUrl, `/t/${topicSlug}`);
  // `@id` = fragment de l'URL canonique du sujet, jamais une URL `/t/<slug>/d/<id>` (B1_01, D1) :
  // une URL déclarée en données structurées est crawlée par Google, et `/d/` canonicalise vers le
  // sujet → coût de crawl plein, gain d'index nul (diagnostic du 2026-09-16, ×121 sur Beauté Test).
  const anchor = (id: string) => `${topicUrl}#${mkMessageAnchor(id)}`;

  return messages.map(message => {
    const pseudo = (message?.author as User)?.profile?.pseudo;
    const authorName = pseudo ?? message?.author?.username ?? 'Anonymous';

    const author: Person = {
      '@type': 'Person',
      name: authorName,
      // `url` = page profil `/p/<pseudo>` (uniquement si le pseudo existe — un invité sans
      // pseudo n'a pas de page profil). Aligne `comment[].author` sur l'author top-level du
      // DiscussionForumPosting (TopicLd.tsx), qui portait déjà cette url : sans elle, Google
      // Search Console signale « Champ "url" manquant dans "comment.author" ».
      // Composée par `profileUrl` (pseudo percent-encodé, source unique B6_12 — ex-B6_11).
      ...(!!pseudo && { url: profileUrl(fullBasepathUrl, pseudo) }),
    };

    return {
      '@type': 'Comment',
      '@id': anchor(message.id as string),
      author,
      text: stripHtml(message.content ?? ''),
      dateCreated: message?.createdAt,
      datePublished: message?.createdAt,
      dateModified: message?.contentEditedAt ?? message?.createdAt,
      discussionUrl: topicUrl,
      // Reference to the parent if it's a reply
      ...(message?.parentMessage?.id
        ? {
            parentItem: {
              '@type': 'Comment',
              '@id': anchor(message.parentMessage.id as string),
            },
          }
        : null),
    };
  });
}
